import { useCallback, useEffect, useState } from 'react'
import { Space, Spin, Switch, Typography, message } from 'antd'
import { featuresApi, type FeatureSettings as Settings } from '../services/privacyApi'

const { Text } = Typography

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

// Подраздел «Доп. функции»: поведение интерфейса, которое каждая команда
// настраивает под себя.
export default function FeatureSettings() {
    const [settings, setSettings] = useState<Settings>({
        mark_read_on_reply_only: false,
        folder_unread_badges: false,
        block_manual_lead_create: false,
        crm_id_only_lead_create: false,
        chat_lead_create_enabled: true,
        profile_visibility_enabled: false,
    })
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const { data } = await featuresApi.getSettings()
            setSettings(data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const toggle = async (field: keyof Settings, checked: boolean) => {
        setSettings((current) => ({ ...current, [field]: checked }))
        try {
            await featuresApi.updateSettings({ [field]: checked })
        } catch (error) {
            setSettings((current) => ({ ...current, [field]: !checked }))
            message.error(extractError(error))
        }
    }

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space size={12} align="start">
                    <Switch
                        checked={settings.mark_read_on_reply_only}
                        onChange={(checked) => toggle('mark_read_on_reply_only', checked)}
                    />
                    <div>
                        <div>Считать прочитанным только после ответа</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Счётчик непрочитанных у диалога гаснет не когда оператор его открыл,
                            а когда ответил лиду. Выключено — счётчик обнуляется при открытии, как раньше.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.folder_unread_badges}
                        onChange={(checked) => toggle('folder_unread_badges', checked)}
                    />
                    <div>
                        <div>Показывать непрочитанные на папках чатов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            На плашке папки появляется красный счётчик — сколько непрочитанных
                            сообщений в диалогах этой папки. Выключено — плашки как раньше,
                            только с количеством чатов.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.crm_id_only_lead_create}
                        onChange={(checked) => toggle('crm_id_only_lead_create', checked)}
                    />
                    <div>
                        <div>Добавлять лида только по ID CRM WW</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Кнопка «Добавить лида» открывает не пустую форму, а окно поиска: вводится номер
                            карточки, по кнопке «Найти» читается карточка из базы CRM Wiseway, показываются
                            её данные и результат проверки на дубли — и только потом человек решает, заводить
                            лида или нет. Выключено — открывается обычная форма заполнения руками.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.chat_lead_create_enabled}
                        onChange={(checked) => toggle('chat_lead_create_enabled', checked)}
                    />
                    <div>
                        <div>Заводить лида из диалога</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Кнопка «Добавить в лиды» появляется у диалога, к которому не привязана карточка —
                            на нём стоит метка «Нет карточки лида». Обычно это человек, который сам написал на
                            наш TG-аккаунт: такие диалоги попадают во вкладку «Ответили» и видны всем.
                            Открывается короткая форма — название, TG и телефон уже подставлены из его профиля,
                            остаётся выбрать гео и температуру; если похожий лид у нас есть, окно предупредит и
                            предложит связать диалог с ним. Первое сообщение такому лиду не уходит: переписка
                            уже идёт. Путь отдельный, поэтому запрет ручного создания и режим «только по ID»
                            его не касаются. Выключено — кнопка скрыта, а запрос отклоняется на сервере.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.block_manual_lead_create}
                        onChange={(checked) => toggle('block_manual_lead_create', checked)}
                    />
                    <div>
                        <div>Запретить ручное создание лида</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Полный запрет: кнопки «Добавить лида» и «Импорт» скрыты у всех, создание
                            отклоняется на сервере, а не только прячется в интерфейсе. Перекрывает и режим
                            добавления по ID. Остаётся только загрузка из CRM Wiseway по источникам.
                        </Text>
                    </div>
                </Space>

                <Text type="secondary" style={{ fontSize: 12 }}>
                    На отчёты о прочтении в Telegram переключатель не влияет: они уходят при открытии
                    диалога в обоих режимах, и лид видит их так же, как и раньше.
                </Text>
            </Space>
        </Spin>
    )
}
